import { createContext, useContext, useState, useCallback, useRef } from "react";

const SidebarContext = createContext(null);

export const SidebarProvider = ({ children }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const justToggledRef = useRef(false);

  const toggleSidebar = () => {
    // Check if touch device (mobile) - simple toggle without lock
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouch) {
      setIsExpanded(prev => !prev);
      setIsLocked(false);
      return;
    }
    // Desktop: toggle lock state
    justToggledRef.current = true;
    
    const newLockedState = !isLocked;
    setIsLocked(newLockedState);
    setIsExpanded(newLockedState);
    
    // Clear the flag after a short delay
    setTimeout(() => {
      justToggledRef.current = false;
    }, 300);
  };

  const expandSidebar = () => {
    setIsExpanded(true);
  };

  const collapseSidebar = () => {
    if (!isLocked && !justToggledRef.current) {
      setIsExpanded(false);
    }
  };

  // Trigger a refresh of sidebar data
  const refreshSidebarStats = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  return (
    <SidebarContext.Provider value={{
      isExpanded,
      setIsExpanded,
      isLocked,
      setIsLocked,
      toggleSidebar,
      expandSidebar,
      collapseSidebar,
      refreshTrigger,
      refreshSidebarStats
    }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};
